import { useEffect, useState, useCallback, type ChangeEvent, type ReactNode } from 'react';
import { ProfilePage } from './ProfilePage';
import { api } from '../lib/api';
import { AuthImage } from '../components/AuthImage';
import type { Paged, StudentView, CampusView, InstitutionView, GovernmentIdView } from '../lib/types';
import { StatusPill } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { ApiError } from '../lib/api';
import { isNative, takePhoto } from '../lib/native';

export function StudentsPage() {
  const [data, setData] = useState<Paged<StudentView> | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [drawer, setDrawer] = useState(false);
  const [guardianFor, setGuardianFor] = useState<StudentView | null>(null);
  const [profileFor, setProfileFor] = useState<StudentView | null>(null);
  const [editFor, setEditFor] = useState<StudentView | null>(null);
  const [lifecycleFor, setLifecycleFor] = useState<StudentView | null>(null);

  // Server-side: the API already supports skip/take/search and returns a total,
  // so we page on the server rather than pulling every student down the wire.
  const load = useCallback(async (q: string, p: number, take: number) => {
    setData(null);                                   // → skeletons
    const params = new URLSearchParams({
      skip: String((p - 1) * take),
      take: String(take),
    });
    if (q) params.set('search', q);
    try {
      setData(await api.get<Paged<StudentView>>(`/api/students?${params}`));
    } catch {
      setData({ items: [], total: 0, skip: 0, take });
    }
  }, []);

  useEffect(() => { load(search, page, size); }, [search, page, size, load]);

  const columns: Column<StudentView>[] = [
    {
      id: 'admissionNumber',
      header: 'Admission #',
      sortable: true,
      value: (s) => s.admissionNumber,
      cell: (s) => <span className="mono">{s.admissionNumber}</span>,
    },
    {
      id: 'fullName',
      header: 'Name',
      primary: true,                                  // ← becomes the card title on mobile
      sortable: true,
      value: (s) => s.fullName,
      cell: (s) => s.fullName,
    },
    {
      id: 'classLabel',
      header: 'Class',
      sortable: true,
      value: (s) => s.classLabel ?? '',
      cell: (s) => s.classLabel ?? '—',
    },
    {
      id: 'guardian',
      header: 'Guardian',
      hideMd: true,                                   // least essential at tablet width
      value: (s) => `${s.guardianName ?? ''} ${s.guardianMobile ?? ''}`,
      cell: (s) => (
        <>
          {s.guardianName ?? '—'}
          {s.guardianMobile && <span style={{ color: 'var(--ink-3)' }}> · {s.guardianMobile}</span>}
        </>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      value: (s) => s.status,
      cell: (s) => <StatusPill status={s.status} />,
    },
    {
      id: 'access',
      header: 'Access',
      actions: true,                                  // ← card footer on mobile
      cell: (s) => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* The whole record in one tap — attendance, marks, fees, certificates, remarks.
              Placed first because it is what someone opening a student list usually wants;
              Guardians is the narrower action. */}
          <button className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setProfileFor(s)}>
            Profile
          </button>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setGuardianFor(s)}>
            Guardians
          </button>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setEditFor(s)}>
            Edit
          </button>
          {/* ⚠ NOT "Delete". A student is a permanent record — this moves them off the roll.
              The wording is the feature: a clerk who reads "Delete" expects the row to vanish. */}
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
            onClick={() => setLifecycleFor(s)}>
            Status
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Records</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Students</h1>
        </div>
        <div className="page-head-actions">
          <button className="btn btn-accent" onClick={() => setDrawer(true)}>+ Enroll student</button>
        </div>
      </div>

      <DataTable
        rows={data?.items ?? null}
        columns={columns}
        rowKey={(s) => s.id}
        search
        searchPlaceholder="Search by name or admission number…"
        onSearch={(q) => { setSearch(q); setPage(1); }}
        total={data?.total}
        page={page}
        pageSize={size}
        onPage={(p, sz) => { setPage(p); setSize(sz); }}
        empty={{
          title: 'No students yet',
          body: 'Enroll the first student to get started.',
          action: <button className="btn btn-accent" onClick={() => setDrawer(true)}>+ Enroll student</button>,
        }}
      />

      {drawer && <EnrollDrawer onClose={() => setDrawer(false)} onDone={() => { setDrawer(false); load(search, page, size); }} />}
      {guardianFor && <GuardianDialog student={guardianFor} onClose={() => setGuardianFor(null)} />}
      {editFor && (
        <EditStudentDialog student={editFor} onClose={() => setEditFor(null)}
          onDone={() => { setEditFor(null); load(search, page, size); }} />
      )}
      {lifecycleFor && (
        <LifecycleDialog student={lifecycleFor} onClose={() => setLifecycleFor(null)}
          onDone={() => { setLifecycleFor(null); load(search, page, size); }} />
      )}
      {profileFor && (
        <ProfilePage personId={profileFor.id} kind="student" onClose={() => setProfileFor(null)} />
      )}
    </div>
  );
}

function EnrollDrawer({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    campusId: '', institutionId: '', admissionNumber: '', fullName: '',
    classLabel: '', guardianName: '', guardianMobile: '', email: '',
    dateOfBirth: '', gender: '', fatherName: '', motherName: '',
  });
  const [campuses, setCampuses] = useState<CampusView[] | null>(null);
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  // load campuses + institutions for the pickers
  useEffect(() => {
    Promise.all([
      api.get<CampusView[]>('/api/campuses'),
      api.get<InstitutionView[]>('/api/institutions'),
    ]).then(([c, i]) => { setCampuses(c); setInstitutions(i); })
      .catch(() => { setCampuses([]); });
  }, []);

  // institutions available for the chosen campus
  const campusInstitutions = institutions.filter((i) => i.campusId === form.campusId);

  const pickCampus = (campusId: string) =>
    setForm((f) => ({ ...f, campusId, institutionId: '' })); // reset institution when campus changes

  const submit = async () => {
    setError(null); setBusy(true);
    try {
      // ⚠ EMPTY STRINGS MUST GO AS null, NOT AS "". An untouched date input reads "", and the API
      // binds dateOfBirth to DateOnly? — "" is not a date, so the whole request 400s and the office
      // sees "could not enroll" on a form where they filled in everything that was required.
      const blank = (v: string) => (v.trim() === '' ? null : v.trim());
      await api.post('/api/students', {
        ...form,
        dateOfBirth: blank(form.dateOfBirth),
        gender: blank(form.gender),
        fatherName: blank(form.fatherName),
        motherName: blank(form.motherName),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not enroll. Check the details and retry.');
    } finally { setBusy(false); }
  };

  const ready = form.campusId && form.institutionId && form.admissionNumber && form.fullName;
  const noCampuses = campuses !== null && campuses.length === 0;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{
        width: 440, maxWidth: '100%', height: '100%', borderRadius: 0, padding: 26, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>New record</div>
          <h2 className="h-display" style={{ fontSize: 22 }}>Enroll student</h2>
        </div>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start' }}>{error}</div>}

        {noCampuses ? (
          <div className="card" style={{ padding: 20, background: 'var(--surface-2)', textAlign: 'center' }}>
            <p style={{ color: 'var(--ink-2)', marginBottom: 6 }}>No campuses set up yet.</p>
            <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>Add a campus and institution under <strong>Campuses &amp; institutions</strong> first, then enroll students here.</p>
          </div>
        ) : (
          <>
            <div>
              <label className="label">Campus</label>
              <select className="input" value={form.campusId} onChange={(e) => pickCampus(e.target.value)}>
                <option value="">{campuses === null ? 'Loading…' : 'Select a campus'}</option>
                {campuses?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Institution</label>
              <select className="input" value={form.institutionId}
                onChange={(e) => setForm({ ...form, institutionId: e.target.value })}
                disabled={!form.campusId}>
                <option value="">
                  {!form.campusId ? 'Select a campus first'
                    : campusInstitutions.length === 0 ? 'No institutions at this campus'
                    : 'Select an institution'}
                </option>
                {campusInstitutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </div>

            <Field label="Admission number" v={form.admissionNumber} on={set('admissionNumber')} placeholder="e.g. 2026-0412" />
            <Field label="Full name" v={form.fullName} on={set('fullName')} placeholder="Student’s full name" />
            <Field label="Class label" v={form.classLabel} on={set('classLabel')} placeholder="e.g. Class 9-A / 2nd PUC PCMB" />
            <Field label="Guardian name" v={form.guardianName} on={set('guardianName')} />
            <Field label="Guardian mobile" v={form.guardianMobile} on={set('guardianMobile')} placeholder="10-digit" />
            <Field label="Email" v={form.email} on={set('email')} placeholder="For fee receipts &amp; notifications" />

            {/* ⚠ ASKED FOR AT THE DOOR BECAUSE A CERTIFICATE NEEDS THEM. A Transfer Certificate must
                carry a date of birth and both parents' names; collected now, they are never retyped
                from paper onto a legal document years later. Blank is allowed — a walk-in admission
                often has a name and a mobile and nothing else. */}
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
              <div className="eyebrow" style={{ marginBottom: 2 }}>For certificates</div>
              <p style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 0, marginBottom: 10 }}>
                A TC needs these. Fill them in now and nobody has to find them later.
              </p>
            </div>
            <div>
              <label className="label">Date of birth</label>
              <input className="input" type="date" value={form.dateOfBirth}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />
            </div>
            <div style={{ marginTop: 12 }}>
              <label className="label">Gender</label>
              {/* Free text behind a datalist: the common three without refusing what a school writes. */}
              <input className="input" list="eduvora-genders" value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })} />
              <datalist id="eduvora-genders">
                <option value="Male" /><option value="Female" /><option value="Transgender" />
              </datalist>
            </div>
            <Field label="Father's name" v={form.fatherName} on={set('fatherName')} />
            <Field label="Mother's name" v={form.motherName} on={set('motherName')} />
          </>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', gap: 10, paddingTop: 12 }}>
          <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!ready || busy} onClick={submit}>
            {busy ? 'Saving…' : 'Enroll'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, placeholder, mono }: { label: string; v: string; on: (e: ChangeEvent<HTMLInputElement>) => void; placeholder?: string; mono?: boolean }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className={`input ${mono ? 'mono' : ''}`} value={v} onChange={on} placeholder={placeholder} />
    </div>
  );
}


/// Staff-facing guardian management for one student: list current links, link an existing user by
/// email, and mint a school access code the family redeems to self-link. Backend enforces
/// tenant-admin/staff on all of these.
function GuardianDialog({ student, onClose }: { student: StudentView; onClose: () => void }) {
  const [links, setLinks] = useState<GuardianLink[]>([]);
  const [email, setEmail] = useState('');
  const [relation, setRelation] = useState('Parent');
  const [code, setCode] = useState<AccessCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setLinks(await api.get<GuardianLink[]>(`/api/admin/students/${student.id}/guardians`)); }
    catch { /* leave empty */ }
  }, [student.id]);
  useEffect(() => { load(); }, [load]);

  const link = async () => {
    if (!email.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/api/admin/students/${student.id}/guardians`, { email: email.trim().toLowerCase(), relation });
      setEmail(''); await load();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not link.'); }
    finally { setBusy(false); }
  };

  const unlink = async (userId: string) => {
    await api.del(`/api/admin/students/${student.id}/guardians/${userId}`);
    await load();
  };

  const mintCode = async (rel: string) => {
    setBusy(true); setError(null);
    try { setCode(await api.post<AccessCode>(`/api/admin/students/${student.id}/access-code`, { relation: rel, expiresInDays: 30 })); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not create a code.'); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 24, width: 460, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>Guardians &amp; access</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>{student.fullName} · {student.admissionNumber}</p>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Linked accounts</div>
        {links.length === 0 ? <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 12 }}>No one linked yet.</p> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {links.map((l) => (
              <div key={l.userId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span className="pill pill-muted" style={{ fontSize: 11 }}>{l.relation}</span>
                <span className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>{l.userId.slice(0, 8)}…</span>
                <button className="btn" style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: 12 }} onClick={() => unlink(l.userId)}>Remove</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ height: 1, background: 'var(--line)', margin: '4px 0 14px' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Link an existing account by email</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="parent@email.com" style={{ flex: 1 }} />
          <select className="input" value={relation} onChange={(e) => setRelation(e.target.value)} style={{ width: 110 }}>
            <option value="Parent">Parent</option>
            <option value="Guardian">Guardian</option>
            <option value="Self">Self</option>
          </select>
          <button className="btn btn-accent" disabled={!email.trim() || busy} onClick={link}>Link</button>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>Or hand the family a code to self-link</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" disabled={busy} onClick={() => mintCode('Parent')}>Parent code</button>
          <button className="btn" disabled={busy} onClick={() => mintCode('Self')}>Student code</button>
        </div>
        {code && (
          <div className="card" style={{ marginTop: 12, padding: 14, textAlign: 'center', background: 'var(--paper,#F5F4F0)' }}>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{code.relation} code</div>
            <div className="mono" style={{ fontSize: 26, fontWeight: 700, letterSpacing: '.12em', margin: '4px 0' }}>{code.code}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>Usable {code.maxUses}×{code.expiresAt ? ` · expires ${code.expiresAt}` : ''}</div>
          </div>
        )}

        <div style={{ display: 'flex', marginTop: 20 }}>
          <button className="btn" style={{ marginLeft: 'auto', justifyContent: 'center' }} onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

interface GuardianLink { userId: string; studentId: string; relation: string; linkedAt: string; }
interface AccessCode { studentId: string; code: string; relation: string; maxUses: number; expiresAt: string | null; }

/** Overlay shell matching GuardianDialog, so the three read the same. */
function DialogShell({ title, subtitle, children, onClose }: {
  title: string; subtitle?: string; children: ReactNode; onClose: () => void;
}) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ padding: 24, width: 460, maxWidth: '92vw', maxHeight: '85vh', overflowY: 'auto' }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 4 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

/**
 * Correct a student's details.
 *
 * ⚠ Admission number is shown but NOT editable. It is the school's permanent handle on this
 * enrolment and is printed on receipts, certificates and the TC — changing it would orphan every
 * paper document already issued.
 */
function EditStudentDialog({ student, onClose, onDone }: {
  student: StudentView; onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState({
    fullName: student.fullName,
    classLabel: student.classLabel ?? '',
    guardianName: student.guardianName ?? '',
    guardianMobile: '',
    email: student.email ?? '',
    // ⚠ Pre-filled from the record, because this dialog is how the six hundred students who were
    // already enrolled ever get a date of birth. Carrying it from a new admission only helps the
    // next child.
    dateOfBirth: student.dateOfBirth ?? '',
    gender: student.gender ?? '',
    fatherName: student.fatherName ?? '',
    motherName: student.motherName ?? '',
    category: student.category ?? '',
    motherTongue: student.motherTongue ?? '',
    bloodGroup: student.bloodGroup ?? '',
    religion: student.religion ?? '',
    nationality: student.nationality ?? '',
    placeOfBirth: student.placeOfBirth ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.put(`/api/students/${student.id}`, {
        fullName: form.fullName.trim(),
        classLabel: form.classLabel.trim() || null,
        guardianName: form.guardianName.trim() || null,
        // ⚠ Blank means "not changed", not "clear it". The list shows a MASKED mobile
        // (+91••••••1234), so sending it back would overwrite the real number with dots.
        guardianMobile: form.guardianMobile.trim() || null,
        email: form.email.trim() || null,
        // ⚠ Same rule as the mobile: null means "leave it alone", never "clear it". The service
        // treats null as untouched, so a blank field here cannot wipe a date of birth.
        dateOfBirth: form.dateOfBirth.trim() || null,
        gender: form.gender.trim() || null,
        fatherName: form.fatherName.trim() || null,
        motherName: form.motherName.trim() || null,
        // ⚠ Same null-means-untouched rule. Six fields on one dialog, and a school that fills in
        // the blood group must not lose the category it entered last month.
        category: form.category.trim() || null,
        motherTongue: form.motherTongue.trim() || null,
        bloodGroup: form.bloodGroup.trim() || null,
        religion: form.religion.trim() || null,
        nationality: form.nationality.trim() || null,
        placeOfBirth: form.placeOfBirth.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <DialogShell title="Edit student" subtitle={`${student.admissionNumber}`} onClose={onClose}>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      {/* ⚠ THE PHOTOGRAPH SITS AT THE TOP, because it is how the office confirms they have the
          right child open before editing anything. Buried at the bottom it would be decoration. */}
      <StudentPhoto studentId={student.id} hasPhoto={student.hasPhoto} />

      <label className="label">Name</label>
      <input className="input" value={form.fullName} style={{ marginBottom: 12 }}
        onChange={(e) => setForm({ ...form, fullName: e.target.value })} />

      <label className="label">Class</label>
      <input className="input" value={form.classLabel} style={{ marginBottom: 12 }}
        onChange={(e) => setForm({ ...form, classLabel: e.target.value })} />

      <label className="label">Guardian name</label>
      <input className="input" value={form.guardianName} style={{ marginBottom: 12 }}
        onChange={(e) => setForm({ ...form, guardianName: e.target.value })} />

      <label className="label">Guardian mobile</label>
      <input className="input" value={form.guardianMobile} placeholder={student.guardianMobile ?? 'Not set'}
        style={{ marginBottom: 4 }}
        onChange={(e) => setForm({ ...form, guardianMobile: e.target.value })} />
      <p style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 12 }}>
        Leave blank to keep the current number.
      </p>

      <label className="label">Email</label>
      <input className="input" value={form.email} style={{ marginBottom: 20 }}
        onChange={(e) => setForm({ ...form, email: e.target.value })} />

      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginBottom: 12 }}>
        <div className="eyebrow">For certificates</div>
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 0' }}>
          A Transfer Certificate must carry these. Filling them in here means nobody types them from
          paper the day a child leaves.
        </p>
      </div>

      <label className="label">Date of birth</label>
      <input className="input" type="date" value={form.dateOfBirth} style={{ marginBottom: 12 }}
        max={new Date().toISOString().slice(0, 10)}
        onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })} />

      <label className="label">Gender</label>
      <input className="input" list="eduvora-genders-edit" value={form.gender} style={{ marginBottom: 12 }}
        onChange={(e) => setForm({ ...form, gender: e.target.value })} />
      <datalist id="eduvora-genders-edit">
        <option value="Male" /><option value="Female" /><option value="Transgender" />
      </datalist>

      <label className="label">Father's name</label>
      <input className="input" value={form.fatherName} style={{ marginBottom: 12 }}
        onChange={(e) => setForm({ ...form, fatherName: e.target.value })} />

      <label className="label">Mother's name</label>
      <input className="input" value={form.motherName} style={{ marginBottom: 20 }}
        onChange={(e) => setForm({ ...form, motherName: e.target.value })} />

      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginBottom: 12 }}>
        <div className="eyebrow">For the statutory returns</div>
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 0' }}>
          SATS and UDISE+ ask for all of these. Filled in now, the return is a download in June
          instead of a fortnight with a paper file.
        </p>
      </div>

      {/* ⚠ Two to a row. Six single-column fields would push the buttons off a laptop screen, and a
          form somebody has to scroll to find "Save" on is a form they abandon half-filled. */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <div>
          <label className="label">Category</label>
          {/* ⚠ A datalist, not a select. Karnataka's own categories are offered, and a school whose
              form has one this software has never heard of can still type it — see Demographics. */}
          <input className="input" list="eduvora-categories" value={form.category}
            style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <datalist id="eduvora-categories">
            <option value="General" /><option value="SC" /><option value="ST" />
            <option value="Cat-1" /><option value="2A" /><option value="2B" />
            <option value="3A" /><option value="3B" /><option value="OBC" /><option value="EWS" />
          </datalist>
        </div>
        <div>
          <label className="label">Blood group</label>
          <input className="input" list="eduvora-bloodgroups" value={form.bloodGroup}
            style={{ marginBottom: 12 }} placeholder="O+"
            onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })} />
          <datalist id="eduvora-bloodgroups">
            <option value="A+" /><option value="A-" /><option value="B+" /><option value="B-" />
            <option value="AB+" /><option value="AB-" /><option value="O+" /><option value="O-" />
          </datalist>
        </div>
        <div>
          <label className="label">Mother tongue</label>
          <input className="input" list="eduvora-tongues" value={form.motherTongue}
            style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, motherTongue: e.target.value })} />
          <datalist id="eduvora-tongues">
            <option value="Kannada" /><option value="Tulu" /><option value="Konkani" />
            <option value="Urdu" /><option value="Hindi" /><option value="Marathi" />
            <option value="Telugu" /><option value="Tamil" /><option value="Malayalam" />
            <option value="English" />
          </datalist>
        </div>
        <div>
          <label className="label">Religion</label>
          <input className="input" value={form.religion} style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, religion: e.target.value })} />
        </div>
        <div>
          <label className="label">Nationality</label>
          <input className="input" value={form.nationality} style={{ marginBottom: 12 }}
            placeholder="Indian"
            onChange={(e) => setForm({ ...form, nationality: e.target.value })} />
        </div>
        <div>
          <label className="label">Place of birth</label>
          <input className="input" value={form.placeOfBirth} style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, placeOfBirth: e.target.value })} />
        </div>
      </div>

      <GovernmentIdPanel studentId={student.id} />

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </DialogShell>
  );
}

/**
 * Move a student's enrolment state.
 *
 * ⚠ There is no delete here and there never will be. A student's invoices, results, attendance and
 * certificates are records the school may be asked to produce years later.
 */
function LifecycleDialog({ student, onClose, onDone }: {
  student: StudentView; onClose: () => void; onDone: () => void;
}) {
  const [action, setAction] = useState(student.status === 'Active' ? 'withdraw' : 'reinstate');
  const [exitDate, setExitDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsReason = action === 'withdraw' || action === 'deactivate';

  const submit = async () => {
    if (needsReason && !reason.trim()) { setError('Please give a reason.'); return; }
    setBusy(true); setError(null);
    try {
      await api.post(`/api/students/${student.id}/${action}`, {
        exitDate: action === 'reinstate' ? null : exitDate,
        reason: reason.trim() || null,
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not update.'); }
    finally { setBusy(false); }
  };

  return (
    <DialogShell title="Enrolment status"
      subtitle={`${student.fullName} · ${student.admissionNumber} · currently ${student.status}`}
      onClose={onClose}>
      {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

      <label className="label">What happened</label>
      <select className="input" value={action} onChange={(e) => { setAction(e.target.value); setError(null); }}
        style={{ marginBottom: 12 }}>
        <option value="withdraw">Left the school (TC issued)</option>
        <option value="alumni">Completed and left</option>
        <option value="deactivate">Temporarily off the roll</option>
        <option value="reinstate">Put back on the roll</option>
      </select>

      {action !== 'reinstate' && (
        <>
          <label className="label">Date</label>
          <input className="input" type="date" value={exitDate} style={{ marginBottom: 12 }}
            onChange={(e) => setExitDate(e.target.value)} />
        </>
      )}

      {needsReason && (
        <>
          <label className="label">Reason</label>
          <input className="input" value={reason} placeholder="Moved city, transferred to another school…"
            style={{ marginBottom: 12 }} onChange={(e) => setReason(e.target.value)} />
        </>
      )}

      {/* ⚠ Says plainly what will and won't happen. A clerk choosing "left the school" needs to
          know the fee ledger survives — otherwise they hesitate, or worse, ask for a delete. */}
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20, lineHeight: 1.6 }}>
        {action === 'reinstate'
          ? 'Only works for a student who is temporarily off the roll. Someone who left the school is re-admitted instead, so their previous enrolment stays a complete record.'
          : 'The student comes off the active roll. Their fees, results, attendance and certificates all stay on record and can still be looked up.'}
      </p>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn btn-accent" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Saving…' : 'Confirm'}
        </button>
      </div>
    </DialogShell>
  );
}

/**
 * One student's photograph, with upload and remove.
 *
 * ⚠ THE IMAGE IS FETCHED WITH THE TOKEN, never linked. A child's photograph behind a plain <img src>
 * would need a URL that works without authentication, and a URL that works without authentication is
 * a URL that keeps working after it has been forwarded. AuthImage does the fetch-and-blob.
 */
function StudentPhoto({ studentId, hasPhoto }: { studentId: string; hasPhoto: boolean }) {
  // ⚠ Local state, seeded from the prop. The dialog does not reload the student after an upload, so
  // reading the prop alone would show "no photograph" beside a photograph that is plainly there.
  const [present, setPresent] = useState(hasPhoto);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ⚠ Bumped after every change to force AuthImage to refetch. Replacing a photograph keeps the same
  // URL, so without this the browser shows the old one from cache and the office thinks it failed.
  const [version, setVersion] = useState(0);

  const send = async (f: File) => {
    setBusy(true); setError(null);
    try {
      const form = new FormData();
      form.append('file', f);
      await api.postForm(`/api/students/${studentId}/photo`, form);
      setPresent(true); setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that photograph.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError(null);
    try {
      await api.del(`/api/students/${studentId}/photo`);
      setPresent(false); setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not remove the photograph.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 16 }}>
      <div style={{
        width: 72, height: 88, borderRadius: 8, overflow: 'hidden', flex: '0 0 auto',
        background: 'var(--surface-2)', border: '1px solid var(--line)',
        display: 'grid', placeItems: 'center', fontSize: 10.5, color: 'var(--ink-3)',
      }}>
        {present
          ? <AuthImage key={version} url={`/api/students/${studentId}/photo?v=${version}`} alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : 'no photo'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <label className="label">Photograph</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* ⚠ INSIDE THE APP, THE CAMERA IS A REAL BUTTON. The file input below already offers
              "Take photo" in a phone browser, but it goes through the system picker, arrives as
              whatever the camera shot (HEIC on an iPhone, which the server refuses) and at full
              resolution. The native path asks for JPEG at 1200px, which is what an ID card needs.
              The input stays for every other case — see native.ts, which returns null rather than
              throwing when there is no camera. */}
          {isNative() && (
            <button type="button" className="btn" disabled={busy}
              onClick={() => void (async () => {
                const f = await takePhoto(`${studentId}.jpg`);
                if (f) await send(f);
              })()}>
              Take a photo
            </button>
          )}

          {/* A label wrapping a hidden input: the one form a phone browser reliably offers
              "Take photo" for. */}
          <label className="btn" style={{ cursor: busy ? 'default' : 'pointer' }}>
            {present ? 'Replace' : 'Add a photo'}
            <input type="file" accept="image/*" disabled={busy} style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                // Cleared, or choosing the same file twice in a row fires no change event.
                e.target.value = '';
                if (f) void send(f);
              }} />
          </label>
          {present && <button className="btn" disabled={busy} onClick={remove}>Remove</button>}
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '6px 0 0' }}>
          Used on the ID card and the register. Up to 5 MB.
        </p>
        {error && <p style={{ fontSize: 12, color: 'var(--danger)', margin: '6px 0 0' }}>{error}</p>}
      </div>
    </div>
  );
}

/**
 * ═══ AADHAAR, APAAR AND SATS ═══
 *
 * ⚠ ITS OWN PANEL WITH ITS OWN SAVE, not part of the dialog's Save. Three reasons, in order of how
 * much they matter: it writes to a different endpoint under a different policy; a validation
 * failure on an Aadhaar must not discard the six demographic fields somebody just typed; and a
 * separate, deliberate action is the right shape for the most sensitive field in the record.
 *
 * ⚠ WHAT LOADS IS MASKED. The full number is never in this component's state until somebody asks
 * for it by name and gives a reason — at which point the server has already written an audit row
 * against the child, with their name on it.
 */
function GovernmentIdPanel({ studentId }: { studentId: string }) {
  const [held, setHeld] = useState<GovernmentIdView | null>(null);
  const [form, setForm] = useState({ aadhaar: '', apaar: '', sats: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let alive = true;
    api.get<GovernmentIdView>(`/api/students/${studentId}/government-ids`)
      .then((g) => {
        if (!alive) return;
        setHeld(g);
        // ⚠ The Aadhaar box starts EMPTY even when one is held, because the only thing we could put
        // in it is the mask — and a form pre-filled with XXXX XXXX 1234 sends those X's back to the
        // server the moment somebody edits the field next to it.
        setForm({ aadhaar: '', apaar: g.apaar ?? '', sats: g.sats ?? '' });
      })
      .catch(() => { if (alive) setHeld(null); });
    return () => { alive = false; };
  }, [studentId]);

  const save = async () => {
    setBusy(true); setError(null); setSaved(false);
    try {
      const g = await api.put<GovernmentIdView>(`/api/students/${studentId}/government-ids`, {
        // ⚠ Blank Aadhaar means "leave the stored one alone" while one is held, and "nothing to
        // store" when none is. Sending '' would CLEAR it — the domain treats empty as a deliberate
        // erase — and clearing a child's Aadhaar because the box looked empty is not recoverable
        // from this screen.
        aadhaar: form.aadhaar.trim() === '' ? (held?.maskedAadhaar ? null : '') : form.aadhaar.trim(),
        apaar: form.apaar.trim(),
        sats: form.sats.trim(),
      });
      setHeld(g);
      setForm({ aadhaar: '', apaar: g.apaar ?? '', sats: g.sats ?? '' });
      setRevealed(null);
      setSaved(true);
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  const reveal = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ aadhaar: string }>(
        `/api/students/${studentId}/government-ids/reveal`, { reason });
      setRevealed(r.aadhaar);
      setAsking(false);
      setReason('');
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not show the number.'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginBottom: 20 }}>
      <div className="eyebrow">Government identifiers</div>
      <p style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '2px 0 12px' }}>
        Kept apart from the rest of the record and visible only to administrators. Looking at a full
        Aadhaar number is recorded against your name in this student's history.
      </p>

      {error && (
        <div className="pill pill-danger"
          style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 10, justifyContent: 'flex-start' }}>
          {error}
        </div>
      )}

      {held?.problems.map((p) => (
        <p key={p} style={{ fontSize: 11.5, color: 'var(--warn, #b54708)', margin: '0 0 8px' }}>{p}</p>
      ))}

      <label className="label">Aadhaar</label>
      <input className="input" value={form.aadhaar} inputMode="numeric"
        placeholder={held?.maskedAadhaar ?? 'Not held'}
        style={{ marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}
        onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <p style={{ fontSize: 11, color: 'var(--ink-3)', margin: 0 }}>
          {held?.maskedAadhaar ? 'Leave blank to keep the stored number.' : 'Twelve digits, from the card.'}
        </p>
        {held?.maskedAadhaar && !revealed && !asking && (
          <button type="button" className="btn btn-ghost" style={{ padding: '2px 8px', fontSize: 11.5 }}
            onClick={() => setAsking(true)}>Show full number</button>
        )}
        {revealed && (
          <span style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
            {revealed}
          </span>
        )}
      </div>

      {asking && (
        <div className="card" style={{ padding: 12, marginBottom: 12, background: 'var(--bg-2, #f7f8fa)' }}>
          <label className="label">Why do you need the full number?</label>
          <input className="input" value={reason} autoFocus
            placeholder="Filing the SATS return for Class X"
            style={{ marginBottom: 8 }}
            onChange={(e) => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
              onClick={() => { setAsking(false); setReason(''); }}>Cancel</button>
            <button type="button" className="btn btn-accent" style={{ padding: '4px 10px', fontSize: 12 }}
              disabled={busy || reason.trim().length < 4} onClick={() => void reveal()}>
              Show it
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
        <div>
          <label className="label">APAAR / PEN</label>
          <input className="input" value={form.apaar} inputMode="numeric" style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, apaar: e.target.value })} />
        </div>
        <div>
          <label className="label">SATS number</label>
          <input className="input" value={form.sats} inputMode="numeric" style={{ marginBottom: 12 }}
            onChange={(e) => setForm({ ...form, sats: e.target.value })} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button type="button" className="btn" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save identifiers'}
        </button>
        {/* ⚠ Said out loud, because this panel has its own button inside a dialog that also has
            one, and a school that presses only the dialog's Save would lose what they typed here. */}
        <span style={{ fontSize: 11, color: saved ? 'var(--ok, #067647)' : 'var(--ink-3)' }}>
          {saved ? 'Saved.' : 'Saved separately from the rest of this form.'}
        </span>
      </div>
    </div>
  );
}
